import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import useFavoritos from '../hooks/useFavoritos';
import { auth, db } from '../firebase';
import { urlSeguraONull } from '../utils/urlSegura';
import { doc, getDoc, updateDoc, increment, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
L.Marker.prototype.options.icon = L.icon({ iconUrl: markerIcon, shadowUrl: markerShadow, iconSize: [25, 41], iconAnchor: [12, 41] });

async function copiarAlPortapapeles(texto) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(texto);
  } else {
    const el = document.createElement('textarea');
    el.value = texto; el.style.position = 'fixed'; el.style.opacity = '0';
    document.body.appendChild(el); el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}

export default function DetallePropiedad() {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	// Volver al catálogo conservando filtros/página: usa el historial si venimos de la app,
	// si no (entrada directa o link compartido) va al inicio.
	const volverAlCatalogo = (e) => {
		e.preventDefault();
		if (location.key && location.key !== 'default') navigate(-1);
		else navigate('/');
	};
	const { toggle, esFavorito } = useFavoritos();
	const [p, setP] = useState(null);
	const [vendedor, setVendedor] = useState(null);
	const [fotoActiva, setFotoActiva] = useState(0);
	const [lightbox, setLightbox] = useState(false);
	const [noEncontrado, setNoEncontrado] = useState(false);
	const [errorCarga, setErrorCarga] = useState(null);
	const sliderRef = useRef(null);

	// Contacto (email y WhatsApp)
	const [contactInfo, setContactInfo] = useState({ email: '', nombre: '' });
	const [agenteWA, setAgenteWA] = useState(null); // { telefono, nombre } si el agente tiene teléfono
	const [formConsulta, setFormConsulta] = useState({ nombre: '', telefono: '' });
	const [consultaEnviada, setConsultaEnviada] = useState(false);
	const [enviandoConsulta, setEnviandoConsulta] = useState(false);
	const [consultaError, setConsultaError] = useState(null);
	const [cotizBlue, setCotizBlue] = useState(null);

	// ── Barra de acciones del corredor/admin ──
	const [usuarioLogueado, setUsuarioLogueado] = useState(null); // { uid, rol, corredorId }
	const [reservando, setReservando] = useState(false);
	const [linkCopiado, setLinkCopiado] = useState(false);
	const [generandoClave, setGenerandoClave] = useState(false);

	useEffect(() => {
		return auth.onAuthStateChanged(async (u) => {
			if (!u) { setUsuarioLogueado(null); return; }
			const snap = await getDoc(doc(db, "usuarios", u.uid));
			if (snap.exists()) {
				const d = snap.data();
				setUsuarioLogueado({ uid: u.uid, rol: d.rol, corredorId: d.corredorId || null });
			}
		});
	}, []);

	useEffect(() => {
		fetch('https://api.bluelytics.com.ar/v2/latest')
			.then(r => r.ok ? r.json() : null)
			.then(d => { const v = d?.blue?.value_sell; if (typeof v === 'number' && v > 0) setCotizBlue(v); })
			.catch(() => {});
	}, []);

	useEffect(() => {
		async function cargar() {
			try {
				const snap = await getDoc(doc(db, "propiedades", id));
				if (!snap.exists()) { setNoEncontrado(true); return; }

				const data = snap.data();
				setP(data);

				let vendedorData = null;
				if (data.vendedorId) {
					const vSnap = await getDoc(doc(db, "vendedores", data.vendedorId));
					if (vSnap.exists()) { vendedorData = vSnap.data(); setVendedor(vendedorData); }
				}

				// Agente de contacto: agenteId explícito tiene prioridad; luego creadoPor si es distinto del corredor
				const agenteUid = data.agenteId || (data.creadoPor && data.creadoPor !== data.vendedorId ? data.creadoPor : null);
				if (agenteUid) {
					const aSnap = await getDoc(doc(db, "usuarios", agenteUid));
					if (aSnap.exists()) {
						const aData = aSnap.data();
						// Email: agente primero, corredor como fallback
						setContactInfo({ email: aData.email || vendedorData?.email || '', nombre: aData.nombre || vendedorData?.nombre || '' });
						// WhatsApp: solo si el agente tiene teléfono
						if (aData.telefono) {
							const digits = aData.telefono.replace(/\D/g, '');
							// Normalizar: si tiene 10 dígitos, asumir Argentina y agregar 54
							const telNorm = digits.length === 10 ? '54' + digits : digits;
							setAgenteWA({ telefono: telNorm, nombre: aData.nombre || '' });
						}
					} else {
						setContactInfo({ email: vendedorData?.email || '', nombre: vendedorData?.nombre || '' });
					}
				} else {
					setContactInfo({ email: vendedorData?.email || '', nombre: vendedorData?.nombre || '' });
				}
			} catch (err) {
				console.error(err);
				setErrorCarga("No se pudo cargar la propiedad. Intentá de nuevo.");
			}
		}
		cargar();
	}, [id]);

	//este es el contador de visitas
	useEffect(() => {
	  const unsubscribe = auth.onAuthStateChanged(async (user) => {
		// 1. Verificamos que no sea un corredor logueado
		// 2. Verificamos que no haya visto YA esta propiedad en esta sesión
		const yaVista = sessionStorage.getItem(`vista_${id}`);

		if (!user && id && id !== "undefined" && !yaVista) {
		  sessionStorage.setItem(`vista_${id}`, "true");
		  const ref = doc(db, "propiedades", id);
		  try {
			await updateDoc(ref, { vistas: increment(1) });
		  } catch (error) {
			console.error("Error al incrementar vistas:", error);
		  }
		  try {
			await addDoc(collection(db, "propiedades", id, "logs"), { tipo: "vista", fecha: serverTimestamp() });
		  } catch (error) {
			console.error("Error al registrar log de vista:", error);
		  }
		}
	  });

	  return () => unsubscribe();
	}, [id]);
	
	//para contar los whatsapp y armar el msg con el ID
	const registrarContacto = async () => {
	  // 1. Evitamos contar contactos de corredores logueados (opcional, igual que las vistas)
	  const user = auth.currentUser;
	  const yaContactado = sessionStorage.getItem(`contacto_${id}`);

	  if (!user && !yaContactado) {
		sessionStorage.setItem(`contacto_${id}`, "true");
		const ref = doc(db, "propiedades", id);
		try {
		  await updateDoc(ref, { contactos: increment(1) });
		} catch (error) {
		  console.error("Error al incrementar contactos:", error);
		}
		try {
		  await addDoc(collection(db, "propiedades", id, "logs"), { tipo: "contacto", fecha: serverTimestamp() });
		} catch (error) {
		  console.error("Error al registrar log de contacto:", error);
		}
	  }
	};

	const guardarConsulta = async () => {
		if (!p?.vendedorId) return;
		try {
			await addDoc(collection(db, "consultas"), {
				corredorId:      p.vendedorId,
				propiedadId:     id,
				propiedadTitulo: p?.titulo || '',
				nombre:          formConsulta.nombre,
				telefono:        formConsulta.telefono,
				email:           '',
				estado:          'nuevo',
				nota:            '',
				creadoEn:        serverTimestamp(),
				actualizadoEn:   serverTimestamp(),
			});
		} catch { /* silencioso — no bloquear el flujo principal */ }
	};

	const abrirMailto = () => {
		const asunto = encodeURIComponent(`Consulta sobre propiedad: ${p?.titulo || ''}`);
		const cuerpo = encodeURIComponent(
			`Hola ${contactInfo.nombre},\n\nMe interesa la propiedad "${p?.titulo || ''}"${p?.refInterna ? ` (Ref: ${p.refInterna})` : ''}.\n\nMis datos:\nNombre: ${formConsulta.nombre}\nTeléfono: ${formConsulta.telefono}\n\nLa vi en FormoProp. Quedo a la espera de su respuesta.\n\nGracias.`
		);
		window.open(`mailto:${contactInfo.email}?subject=${asunto}&body=${cuerpo}`);
		registrarContacto();
		guardarConsulta();
		setConsultaEnviada(true);
		setFormConsulta({ nombre: '', telefono: '' });
	};

	const enviarConsulta = async (e) => {
		e.preventDefault();
		if (!contactInfo.email) return;
		setEnviandoConsulta(true);
		setConsultaError(null);
		try {
			const res = await fetch('/api/enviar-consulta.php', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'X-Api-Token': process.env.REACT_APP_API_TOKEN || '' },
				body: JSON.stringify({
					to_email:      contactInfo.email,
					to_nombre:     contactInfo.nombre,
					from_nombre:   formConsulta.nombre,
					from_telefono: formConsulta.telefono,
					prop_titulo:   p?.titulo     || '',
					prop_ref:      p?.refInterna || '',
				}),
			});
			// Intentamos parsear JSON; si falla (PHP no instalado, 404, etc.) usamos mailto
			let json = null;
			try { json = await res.json(); } catch { /* respuesta no-JSON */ }

			if (json?.ok) {
				registrarContacto();
				guardarConsulta();
				setConsultaEnviada(true);
				setFormConsulta({ nombre: '', telefono: '' });
			} else if (json?.error) {
				// PHP respondió pero con error de envío (credenciales, SMTP, etc.)
				setConsultaError(json.error);
			} else {
				// PHP no disponible (localhost, archivo no subido) → fallback silencioso
				abrirMailto();
			}
		} catch {
			// Red cortada o PHP no encontrado → fallback silencioso
			abrirMailto();
		}
		setEnviandoConsulta(false);
	};

	// Hooks ANTES de los early returns para no violar Rules of Hooks
	const fotosReales = p ? (p.fotosUrls?.length ? p.fotosUrls : [p.fotoUrl]).filter(Boolean) : [];
	const fotos = fotosReales.length ? fotosReales : ['/sin-imagen.svg'];
	// Miniaturas para el strip (mucho más livianas); cae a la foto full si no hay thumb.
	const fotosThumbs = p ? (p.fotosThumbs?.length ? p.fotosThumbs : []) : [];
	const thumbDe = (idx) => fotosThumbs[idx] || fotos[idx];
	const prevFoto = useCallback((e) => { e?.stopPropagation(); setFotoActiva(i => (i - 1 + fotos.length) % fotos.length); }, [fotos.length]);
	const nextFoto = useCallback((e) => { e?.stopPropagation(); setFotoActiva(i => (i + 1) % fotos.length); }, [fotos.length]);
	const scrollSlider = (dir) => sliderRef.current?.scrollBy({ left: dir * 220, behavior: 'smooth' });

	// Precarga las fotos en alta en segundo plano: así al clickear una miniatura
	// la imagen grande ya está en caché del navegador y el cambio es instantáneo
	// (sin esperar la descarga de la foto full cada vez).
	const fotosKey = fotos.join('|');
	useEffect(() => {
		fotos.forEach((url) => { const img = new Image(); img.src = url; });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fotosKey]);

    if (errorCarga) return (
      <div style={{padding: '50px', textAlign: 'center'}}>
        <p style={{color: '#dc3545', fontSize: '16px'}}>{errorCarga}</p>
        <Link to="/" style={{color: '#007bff'}}>← Volver al catálogo</Link>
      </div>
    );
    if (noEncontrado) return (
      <div style={{padding: '50px', textAlign: 'center'}}>
        <h2 style={{color: '#333'}}>Propiedad no encontrada</h2>
        <p style={{color: '#888'}}>Es posible que haya sido eliminada o el enlace sea incorrecto.</p>
        <Link to="/" style={{color: '#007bff', fontWeight: 'bold'}}>← Volver al catálogo</Link>
      </div>
    );
    if (!p) return <div style={{padding: '50px', textAlign: 'center'}}>Cargando ficha de la propiedad...</div>;

	// WhatsApp: si el agente tiene teléfono lo contactamos a él, sino al corredor
	const numeroWa = agenteWA?.telefono || vendedor?.whatsapp || "5493704000000";
	const nombreWa  = agenteWA ? agenteWA.nombre : (vendedor?.nombre || '');
	const refTexto = p.refInterna ? ` (Ref: ${p.refInterna})` : '';
	const mensajeWa = `Hola ${nombreWa}, me interesa la propiedad: ${p.titulo}${refTexto}. La vi en FormoProp.`;
	const urlWa = `https://wa.me/${numeroWa}?text=${encodeURIComponent(mensajeWa)}`;

  return (
    <div className="detalle-wrap" style={{ padding: '40px', maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>
    <style>{`
      @media (max-width: 820px) {
        .detalle-grid { grid-template-columns: 1fr !important; }
        .detalle-wrap { padding: 20px !important; }
      }
    `}</style>

      {/* ── LIGHTBOX ── */}
      {lightbox && (
        <div onClick={() => setLightbox(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Cerrar */}
          <button onClick={() => setLightbox(false)} style={{ position: 'absolute', top: '18px', right: '22px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '26px', cursor: 'pointer', borderRadius: '50%', width: '44px', height: '44px', lineHeight: 1 }}>✕</button>
          {/* Anterior */}
          {fotos.length > 1 && <button onClick={prevFoto} style={{ position: 'absolute', left: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '32px', cursor: 'pointer', borderRadius: '50%', width: '52px', height: '52px', lineHeight: 1 }}>‹</button>}
          {/* Imagen */}
          <img src={fotos[fotoActiva]} alt="fullsize" onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '10px', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }} />
          {/* Siguiente */}
          {fotos.length > 1 && <button onClick={nextFoto} style={{ position: 'absolute', right: '16px', background: 'rgba(255,255,255,0.12)', border: 'none', color: 'white', fontSize: '32px', cursor: 'pointer', borderRadius: '50%', width: '52px', height: '52px', lineHeight: 1 }}>›</button>}
          {/* Contador */}
          <p style={{ position: 'absolute', bottom: '18px', color: 'rgba(255,255,255,0.5)', fontSize: '13px', margin: 0 }}>{fotoActiva + 1} / {fotos.length}</p>
        </div>
      )}

      <a href="/" onClick={volverAlCatalogo} style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer' }}>← Volver al listado</a>

      {/* ── BARRA DE ACCIONES DEL CORREDOR ── */}
      {usuarioLogueado && p && (
        usuarioLogueado.rol === 'admin' ||
        usuarioLogueado.uid === p.vendedorId ||
        (usuarioLogueado.rol === 'agente' && usuarioLogueado.corredorId === p.vendedorId)
      ) && (
        <div style={{ margin: '12px 0', padding: '10px 16px', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px' }}>
          <span style={{ fontWeight: '700', color: '#4338ca', marginRight: '4px' }}>Acciones:</span>
          <button
            onClick={() => navigate(`/panel?editar=${id}`)}
            style={{ padding: '6px 14px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
          >
            ✏️ Editar
          </button>
          <button
            onClick={() => navigate(`/estadisticas/${id}`)}
            style={{ padding: '6px 14px', background: '#0369a1', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
          >
            📊 Estadísticas
          </button>
          <button
            disabled={reservando}
            onClick={async () => {
              setReservando(true);
              try {
                await updateDoc(doc(db, "propiedades", id), { reservado: !p.reservado });
                setP(prev => ({ ...prev, reservado: !prev.reservado }));
              } catch (err) { console.error(err); }
              setReservando(false);
            }}
            style={{ padding: '6px 14px', background: p.reservado ? '#d97706' : '#f0f0f0', color: p.reservado ? 'white' : '#555', border: p.reservado ? 'none' : '1px solid #ccc', borderRadius: '6px', cursor: reservando ? 'default' : 'pointer', fontWeight: '600', fontSize: '13px' }}
          >
            {p.reservado ? '🔖 Quitar reserva' : '🔖 Reservar'}
          </button>
          {vendedor?.placasHabilitadas && (
            <button
              onClick={() => navigate(`/panel?placa=${id}`)}
              style={{ padding: '6px 14px', background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }}
            >
              📣 Generar placa
            </button>
          )}
          <button
            disabled={generandoClave}
            onClick={async () => {
              setGenerandoClave(true);
              try {
                let clave = p.claveEstadisticas;
                if (!clave) {
                  clave = crypto.randomUUID();
                  await updateDoc(doc(db, "propiedades", id), { claveEstadisticas: clave });
                  setP(prev => ({ ...prev, claveEstadisticas: clave }));
                }
                const url = `${window.location.origin}/estadisticas-propiedad/${id}?clave=${clave}`;
                await copiarAlPortapapeles(url);
                setLinkCopiado(true);
                setTimeout(() => setLinkCopiado(false), 3000);
              } catch (err) { console.error(err); }
              setGenerandoClave(false);
            }}
            style={{ padding: '6px 14px', background: linkCopiado ? '#059669' : '#f0fdf4', color: linkCopiado ? 'white' : '#059669', border: linkCopiado ? 'none' : '1px solid #a7f3d0', borderRadius: '6px', cursor: generandoClave ? 'default' : 'pointer', fontWeight: '600', fontSize: '13px', transition: 'all 0.2s' }}
          >
            {linkCopiado ? '✅ Link copiado' : '📋 Compartir estadísticas'}
          </button>
        </div>
      )}

      <div className="detalle-grid" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '40px', marginTop: '20px' }}>

        {/* COLUMNA IZQUIERDA: GALERÍA Y DESCRIPCIÓN */}
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          {/* Galería */}
          <div style={{ background: 'white', padding: '15px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            {/* Foto principal */}
            <div style={{ position: 'relative', cursor: 'zoom-in', background: '#f0f0f0', borderRadius: '10px', overflow: 'hidden', height: '460px' }} onClick={() => setLightbox(true)}>
              {/* Fondo borroso (miniatura) para rellenar las franjas en fotos apaisadas/verticales */}
              <img src={thumbDe(fotoActiva)} alt="" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(22px)', transform: 'scale(1.15)', opacity: 0.6 }} />
              {/* Foto completa, sin recortar */}
              <img src={fotos[fotoActiva]} onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/sin-imagen.svg'; }} alt="Principal" style={{ position: 'relative', width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
              <span style={{ position: 'absolute', bottom: '10px', right: '12px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}>
                🔍 Ver en grande
              </span>
              {fotos.length > 1 && (
                <span style={{ position: 'absolute', bottom: '10px', left: '12px', background: 'rgba(0,0,0,0.55)', color: 'white', fontSize: '11px', padding: '4px 10px', borderRadius: '20px' }}>
                  {fotoActiva + 1} / {fotos.length}
                </span>
              )}
            </div>

            {/* Slider de miniaturas */}
            {fotos.length > 1 && (
              <div style={{ position: 'relative', marginTop: '12px' }}>
                {fotos.length > 4 && (
                  <button onClick={() => scrollSlider(-1)} style={btnSlider('left')}>‹</button>
                )}
                <div ref={sliderRef} style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {fotos.map((url, idx) => (
                    <img key={idx} src={thumbDe(idx)} alt={`foto ${idx + 1}`} loading="lazy" decoding="async"
                      onClick={() => setFotoActiva(idx)}
                      style={{ width: '88px', height: '66px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0, cursor: 'pointer', transition: 'all 0.15s', border: fotoActiva === idx ? '3px solid #007bff' : '2px solid transparent', opacity: fotoActiva === idx ? 1 : 0.75 }}
                    />
                  ))}
                </div>
                {fotos.length > 4 && (
                  <button onClick={() => scrollSlider(1)} style={btnSlider('right')}>›</button>
                )}
              </div>
            )}
          </div>

          {/* ── TOUR VIRTUAL ── */}
          {urlSeguraONull(p.tourVirtualUrl) && (
            <div style={{ marginTop: '20px' }}>
              <a
                href={p.tourVirtualUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  background: '#28a745', color: 'white', padding: '12px 24px',
                  borderRadius: '10px', fontWeight: '700', fontSize: '15px',
                  textDecoration: 'none', boxShadow: '0 2px 8px rgba(40,167,69,0.3)',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#218838'}
                onMouseLeave={e => e.currentTarget.style.background = '#28a745'}
              >
                🏠 Tour Virtual
              </a>
            </div>
          )}

          {/* Descripción larga */}
          <div style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <h3>Descripción de la Propiedad</h3>
            <p style={{ color: '#444', lineHeight: '1.8', whiteSpace: 'pre-wrap' }}>{p.descripcion}</p>
          </div>
        </div>

        {/* COLUMNA DERECHA: DATOS DUROS Y CONTACTO */}
        <div style={{ minWidth: 0 }}>
          <div style={{ background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ background: '#eef2ff', color: '#4f46e5', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
                {p.tipoPropiedad} {p.subtipo && `(${p.subtipo})`} - {p.operacion}
              </span>
              {p.reservado && (
                <span style={{ background: '#d97706', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
                  🔖 RESERVADO
                </span>
              )}
              {p.refInterna && (
                <span style={{ background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold', border: '1px solid #e2e8f0' }}>
                  Ref: {p.refInterna}
                </span>
              )}
            </div>
            <h1 style={{ margin: '15px 0 8px', fontSize: '24px', color: '#1a1a1a' }}>{p.titulo}</h1>
            {(p.direccion || p.barrio || p.ciudad) && (
              <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#555', display: 'flex', alignItems: 'flex-start', gap: '4px', lineHeight: 1.4 }}>
                <span>📍</span>
                <span>{[p.direccion, p.barrio, p.ciudad, p.provincia && p.provincia !== 'Formosa' ? p.provincia : null].filter(Boolean).join(', ')}</span>
              </p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px', margin: '0 0 20px 0' }}>
              <div>
                <p style={{ fontSize: '36px', fontWeight: 'bold', color: '#1a1a1a', margin: 0 }}>
                  {p.precio ? `${p.moneda === 'USD' ? 'U$S' : '$'} ${Number(p.precio).toLocaleString('es-AR')}` : 'Consultar precio'}
                </p>
                {cotizBlue && p.precio > 0 && (
                  p.moneda === 'USD'
                    ? <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>≈ $ {Math.round(p.precio * cotizBlue).toLocaleString('es-AR')} <span style={{ fontSize: '11px' }}>(dólar blue)</span></p>
                    : <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>≈ U$S {Math.round(p.precio / cotizBlue).toLocaleString('es-AR')} <span style={{ fontSize: '11px' }}>(dólar blue)</span></p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => toggle(id)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: esFavorito(id) ? '#fff1f2' : '#f9fafb', border: `1px solid ${esFavorito(id) ? '#fda4af' : '#e5e7eb'}`, borderRadius: '20px', color: esFavorito(id) ? '#e11d48' : '#6b7280', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {esFavorito(id) ? '♥ Guardada' : '♡ Guardar'}
                </button>
                <button
                  onClick={async () => {
                    const url = window.location.href;
                    const texto = `${p.titulo} — ${p.precio ? `${p.moneda === 'USD' ? 'U$S' : '$'} ${Number(p.precio).toLocaleString('es-AR')}` : 'Consultar precio'}`;
                    if (navigator.share) {
                      try { await navigator.share({ title: p.titulo, text: texto, url }); } catch {}
                    } else {
                      await copiarAlPortapapeles(url);
                      alert('¡Link copiado al portapapeles!');
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '20px', color: '#0369a1', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  🔗 Compartir
                </button>
              </div>
            </div>
            {p.expensas && <p style={{ color: '#666', marginTop: '-15px' }}>+ ${p.expensas} de expensas</p>}

            {/* FICHA TÉCNICA (GRILLA) */}
            <h3 style={{ borderBottom: '2px solid #f0f2f5', paddingBottom: '10px', marginTop: '30px' }}>Características</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px', color: '#555' }}>
              {p.m2Totales && <div>📐 <strong>Total:</strong> {p.m2Totales} m²</div>}
              {p.m2Cubiertos && <div>🏠 <strong>Cubiertos:</strong> {p.m2Cubiertos} m²</div>}
              {p.m2Semicubiertos && <div>🏗️ <strong>Semicubiertos:</strong> {p.m2Semicubiertos} m²</div>}
              {p.habitaciones && <div>🛏️ <strong>Habitaciones:</strong> {p.habitaciones}</div>}
              {p.banos && <div>🚿 <strong>Baños:</strong> {p.banos}</div>}
              {p.cocheras && <div>🚗 <strong>Cocheras:</strong> {p.cocheras}</div>}
              {p.plantas && <div>🏢 <strong>Plantas:</strong> {p.plantas}</div>}
              {p.antiguedad && <div>⏳ <strong>Antigüedad:</strong> {p.antiguedad === "0" ? "A estrenar" : `${p.antiguedad} años`}</div>}
              {p.estado && <div>✨ <strong>Estado:</strong> {p.estado}</div>}
              {(p.frente || p.fondo) && <div>📏 <strong>Lote:</strong> {p.frente ? `${p.frente}m` : '?'} x {p.fondo ? `${p.fondo}m` : '?'}</div>}
              {p.aptoCredito && <div>🏦 <strong>Apto crédito hipotecario</strong></div>}
              {p.aptoCreditoSinPlano && <div>🏦 <strong>Apto crédito sin plano</strong> <span style={{ color: '#888', fontSize: '13px' }}>(no requiere plano actualizado)</span></div>}
            </div>

            {/* CONDICIONES ALQUILER */}
            {p.operacion === 'Alquiler' && (p.indicadorAumento || p.periodoAumento || (p.duracionContrato && parseInt(p.duracionContrato) > 0) || p.mesesGarPagere || p.mesesGarEfectivo || p.mesesDepEspecial) && (
              <div style={{ marginTop: '20px', padding: '16px 18px', background: '#f0f7ff', border: '1px solid #bfdbfe', borderRadius: '10px', fontSize: '14px', color: '#1e40af' }}>
                <p style={{ margin: '0 0 10px', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>📋 Condiciones del alquiler</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 24px' }}>
                  {p.duracionContrato && parseInt(p.duracionContrato) > 0 && (
                    <span>⏱ <strong>Duración:</strong> {p.duracionContrato} meses</span>
                  )}
                  {(p.indicadorAumento || p.periodoAumento) && (() => {
                    const periodoTxt = p.periodoAumento
                      ? (/^\d+$/.test(String(p.periodoAumento).trim())
                          ? `cada ${p.periodoAumento} ${String(p.periodoAumento).trim() === '1' ? 'mes' : 'meses'}`
                          : p.periodoAumento)
                      : '';
                    return (
                      <span>📈 <strong>Ajuste:</strong> {p.indicadorAumento || ''}{p.indicadorAumento && periodoTxt ? ' · ' : ''}{periodoTxt}</span>
                    );
                  })()}
                  {p.mesesGarPagere && (
                    <span>📄 <strong>Garantía pagaré:</strong> {p.mesesGarPagere} {p.mesesGarPagere === '1' ? 'mes' : 'meses'}</span>
                  )}
                  {p.mesesGarEfectivo && (
                    <span>💵 <strong>Garantía efectivo:</strong> {p.mesesGarEfectivo} {p.mesesGarEfectivo === '1' ? 'mes' : 'meses'}</span>
                  )}
                  {p.mesesDepEspecial && (
                    <span>🔒 <strong>Depósito especial:</strong> {p.mesesDepEspecial}</span>
                  )}
                </div>
              </div>
            )}

            {/* PLANO */}
            {p.poseePlano && urlSeguraONull(p.planoUrl) && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ borderBottom: '2px solid #f0f2f5', paddingBottom: '10px' }}>Plano</h3>
                <a href={urlSeguraONull(p.planoUrl)} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', background: '#1a1a1a', color: 'white', borderRadius: '8px', textDecoration: 'none', fontSize: '14px', fontWeight: 'bold' }}>
                  📄 Ver plano completo
                </a>
              </div>
            )}
            {p.poseePlano && !p.planoUrl && (
              <div style={{ marginTop: '20px', padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', color: '#6b7280' }}>
                📋 Esta propiedad posee plano. Consultá al corredor para obtenerlo.
              </div>
            )}

            {/* BOTONES DE ACCIÓN */}
            <div style={{ marginTop: '40px' }}>
              <a 
				  href={urlWa}
				  onClick={registrarContacto} // <-- Llamamos a la función al hacer clic
				  target="_blank" 
				  rel="noreferrer" 
				  style={{ 
					display: 'block', 
					textAlign: 'center', 
					background: '#25D366', 
					color: 'white', 
					padding: '15px', 
					borderRadius: '8px', 
					textDecoration: 'none', 
					fontWeight: 'bold', 
					fontSize: '16px' 
				  }}
				>
                💬 Contactar por WhatsApp
              </a>
              
              {urlSeguraONull(p.ubicacionMap) && (
                <a href={urlSeguraONull(p.ubicacionMap)} target="_blank" rel="noreferrer" style={{ display: 'block', textAlign: 'center', border: '2px solid #ddd', color: '#555', padding: '12px', borderRadius: '8px', textDecoration: 'none', marginTop: '10px', fontWeight: 'bold' }}>
                  📍 Ver ubicación en el Mapa
                </a>
              )}

              {urlSeguraONull(p.sitioWeb) && (
                <a href={urlSeguraONull(p.sitioWeb)} target="_blank" rel="noreferrer" style={{ display: 'block', textAlign: 'center', border: '2px solid #007bff', color: '#007bff', padding: '12px', borderRadius: '8px', textDecoration: 'none', marginTop: '10px', fontWeight: 'bold' }}>
                  🌐 Ver propiedad en el sitio del corredor
                </a>
              )}

              {/* FORMULARIO DE CONSULTA POR EMAIL */}
              {contactInfo.email && (
                <div style={{ marginTop: '16px', background: '#f0f4ff', borderRadius: '12px', padding: '18px 20px', border: '1px solid #dbe8ff' }}>
                  <h4 style={{ margin: '0 0 14px', fontSize: '15px', color: '#1a1a1a' }}>✉️ Consultar por email</h4>
                  {consultaEnviada ? (
                    <div style={{ textAlign: 'center', padding: '12px 0' }}>
                      <p style={{ fontSize: '28px', margin: '0 0 8px' }}>✅</p>
                      <p style={{ margin: '0 0 4px', fontWeight: 'bold', color: '#155724', fontSize: '15px' }}>¡Consulta enviada!</p>
                      <p style={{ margin: '0 0 12px', color: '#555', fontSize: '13px' }}>El corredor/agente recibirá tu nombre y teléfono y se contactará con vos.</p>
                      <button onClick={() => { setConsultaEnviada(false); setConsultaError(null); }} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}>
                        Enviar otra consulta
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={enviarConsulta}>
                      <input
                        placeholder="Tu nombre completo *"
                        value={formConsulta.nombre}
                        onChange={e => setFormConsulta(f => ({ ...f, nombre: e.target.value }))}
                        required
                        style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '7px', border: '1.5px solid #c7d9f8', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
                      />
                      <input
                        placeholder="Tu teléfono *"
                        value={formConsulta.telefono}
                        onChange={e => setFormConsulta(f => ({ ...f, telefono: e.target.value }))}
                        required
                        style={{ width: '100%', padding: '10px 12px', marginBottom: '8px', borderRadius: '7px', border: '1.5px solid #c7d9f8', boxSizing: 'border-box', fontSize: '14px', outline: 'none' }}
                      />
                      {consultaError && (
                        <p style={{ margin: '0 0 8px', color: '#dc3545', fontSize: '13px', background: '#fff5f5', padding: '8px 10px', borderRadius: '6px' }}>
                          ⚠️ {consultaError}
                        </p>
                      )}
                      <button type="submit" disabled={enviandoConsulta} style={{ width: '100%', padding: '11px', background: enviandoConsulta ? '#555' : '#1a1a1a', color: 'white', border: 'none', borderRadius: '7px', cursor: enviandoConsulta ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                        {enviandoConsulta ? 'Enviando...' : 'Enviar consulta →'}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
			
			
			
			

            {/* TARJETA DEL VENDEDOR */}
		
			{vendedor && (
			  <Link
			    to={vendedor.slug ? `/${vendedor.slug}` : `/corredor/${p.vendedorId}`}
			    style={{ display: 'block', textDecoration: 'none', color: 'inherit', marginTop: '30px' }}
			  >
			    <div
			      style={{ padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #eee', cursor: 'pointer', transition: 'box-shadow 0.2s, transform 0.2s' }}
			      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
			      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
			    >
				  <p style={{ fontSize: '11px', color: '#007bff', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '10px' }}>
				    Corredor Inmobiliario Responsable
				  </p>
				  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
				    {vendedor.fotoPerfilUrl
				      ? <img src={vendedor.fotoPerfilUrl} style={{ width: '54px', height: '54px', objectFit: 'cover', borderRadius: '50%', flexShrink: 0, border: '2px solid #eee' }} alt="foto" />
				      : vendedor.logoUrl
				        ? <img src={vendedor.logoUrl} style={{ width: '54px', height: '54px', objectFit: 'contain', flexShrink: 0 }} alt="logo" />
				        : <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>👤</div>
				    }
				    <div>
					  <p style={{ margin: 0, fontWeight: 'bold', color: '#1a1a1a', fontSize: '16px' }}>
					    {vendedor.nombre}
					  </p>
					  {vendedor.logoUrl && vendedor.fotoPerfilUrl && (
					    <img src={vendedor.logoUrl} style={{ display: 'block', height: '22px', objectFit: 'contain', marginTop: '4px', opacity: 0.7 }} alt="logo" />
					  )}
					  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#007bff', fontWeight: '600' }}>Ver cartera completa →</p>
				    </div>
				  </div>
			    </div>
			  </Link>
			)}
			

          </div>
        </div>
      </div>

      {/* ── VIDEO DE YOUTUBE ── */}
      {(() => {
        const m = (p.videoYoutube || '').match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/);
        const videoId = m ? m[1] : null;
        return videoId ? (
          <div style={{ marginTop: '30px', background: 'white', padding: '24px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a1a1a' }}>▶️ Video de la propiedad</h3>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title="Video de la propiedad"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              />
            </div>
          </div>
        ) : null;
      })()}

      {/* ── MAPA DE UBICACIÓN ── */}
      {p.lat && p.lng && (
        <div style={{ marginTop: '30px', background: 'white', padding: '24px', borderRadius: '15px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: '#1a1a1a' }}>📍 Ubicación</h3>
          <div style={{ height: '320px', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <MapContainer center={[p.lat, p.lng]} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              <Marker position={[p.lat, p.lng]} />
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
}

const btnSlider = (side) => ({
  position: 'absolute', [side]: 0, top: '50%', transform: 'translateY(-50%)',
  zIndex: 1, background: 'rgba(0,0,0,0.45)', border: 'none', color: 'white',
  fontSize: '22px', cursor: 'pointer', borderRadius: '4px',
  width: '28px', height: '66px', lineHeight: 1, padding: 0,
});